import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import { accountingError, dollarsToCents, formatCents } from '@/lib/accounting';
import { getAccountingLedgerAccounts, recordAccountingInvoicePayment, updateAccountingInvoicePayment } from '@/lib/api';
import { localDateInputValue } from '@/lib/dates';
import { cn } from '@/lib/utils';

/**
 * Record money received against one invoice.
 *
 * Manual only, deliberately: there is no card charge and no bank feed behind
 * this, and a button that pretends otherwise would be worse than its absence.
 *
 * The Account field is the one part that is not about the invoice. A recorded
 * payment already appears in Accounting → Transactions — that list reads the
 * payment rather than holding a copy of it — so nothing is created there. What
 * the projection cannot know is which of the college's accounts the money
 * landed in, because only the person taking it does. Answering it here saves
 * filing the same payment twice, and leaving it blank is allowed: the payment
 * is the fact, the account is bookkeeping about it, and Transactions can file
 * it later.
 *
 * The idempotency key is generated once when the dialog opens, so a
 * double-clicked Save is the same payment rather than two.
 */

function todayInput() {
  return localDateInputValue();
}

function newIdempotencyKey() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `pay-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function InvoicePaymentDialog({
  invoice,
  payment,
  open,
  onOpenChange,
  methods,
  onRecorded,
}) {
  const [form, setForm] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    if (!open || (!invoice && !payment)) return;
    setForm({
      amount: ((payment?.amount_cents ?? invoice?.amount_due_cents ?? 0) / 100).toFixed(2),
      payment_date: payment ? payment.payment_date : todayInput(),
      method: payment?.method || '',
      reference: payment?.reference || '',
      note: payment?.note || '',
      account_id: '',
      idempotency_key: newIdempotencyKey(),
    });
  }, [open, invoice, payment]);

  useEffect(() => {
    if (!open || payment) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAccountingLedgerAccounts();
        if (cancelled) return;
        const payment = data?.payment_accounts || [];
        setAccounts(payment);
        // Pre-select the account the college banks into, so the common case is
        // one fewer decision. It stays changeable.
        const preferred = payment.find((row) => row.is_default_payment_account) || payment[0];
        if (preferred) {
          setForm((current) => (current ? { ...current, account_id: preferred.id } : current));
        }
      } catch {
        // The field degrades to "not filed yet", which Transactions can fix.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, payment]);

  const amountCents = dollarsToCents(form?.amount);
  const dueCents = invoice?.amount_due_cents || 0;
  const overpaying = amountCents > dueCents && dueCents >= 0;
  const remaining = Math.max(dueCents - amountCents, 0);

  const methodOptions = useMemo(
    () => [...new Set([...(methods || []), ...(form?.method ? [form.method] : [])])],
    [methods, form?.method]
  );

  const submit = async () => {
    if (submitting.current) return;
    if (amountCents <= 0) {
      toast.error('A payment has to be more than zero.');
      return;
    }
    if (!form.payment_date) {
      toast.error('Enter the date the payment was received.');
      return;
    }
    submitting.current = true;
    setSaving(true);
    try {
      if (payment) {
        await updateAccountingInvoicePayment(payment.transaction_ref, {
          amount: Number(form.amount), payment_date: form.payment_date,
          method: form.method, reference: form.reference, note: form.note,
        });
        onOpenChange(false);
        toast.success('Payment updated.');
        await onRecorded?.();
        return;
      }
      const { data } = await recordAccountingInvoicePayment(invoice.ref, {
        amount: Number(String(form.amount).replace(/[$,]/g, '')),
        payment_date: form.payment_date,
        method: form.method,
        reference: form.reference,
        note: form.note,
        account_id: form.account_id || '',
        idempotency_key: form.idempotency_key,
      });
      onOpenChange(false);
      toast.success(
        data?.status === 'paid'
          ? `Payment recorded. Invoice ${data.number} is paid in full.`
          : `Payment recorded. ${formatCents(data?.amount_due_cents ?? 0)} still due.`
      );
      await onRecorded?.(data);
    } catch (err) {
      toast.error(accountingError(err, 'The payment could not be recorded.'));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{payment ? 'Edit payment' : 'Record a payment'}</DialogTitle>
          <DialogDescription>
            {payment && 'Update the saved payment. Balances update in Billing and Accounting.'}
            {invoice && (
              <>
                Invoice {invoice.number} · {invoice.amount_due_display} outstanding
                {invoice.customer?.source_type === 'student' && (
                  <span className="mt-1 block text-xs">
                    This is recorded on the student&apos;s tuition ledger, so their finance
                    record moves with it.
                  </span>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="payment-amount">Amount received</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(event) => setForm({ ...form, amount: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="payment-date">Payment date</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={form.payment_date}
                  onChange={(event) => setForm({ ...form, payment_date: event.target.value })}
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
              <div>
                <Label htmlFor="payment-method">Method</Label>
                <select
                  id="payment-method"
                  value={form.method}
                  onChange={(event) => setForm({ ...form, method: event.target.value })}
                  className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
                >
                  <option value="">Not stated</option>
                  {methodOptions.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="payment-reference">Reference</Label>
                <Input
                  id="payment-reference"
                  value={form.reference}
                  onChange={(event) => setForm({ ...form, reference: event.target.value })}
                  placeholder="Cheque number, transfer id"
                  className={cn('mt-1', adminInputClass)}
                />
              </div>
            </div>

            {!payment && <div>
              <Label htmlFor="payment-account">Deposited into</Label>
              <select
                id="payment-account"
                value={form.account_id}
                onChange={(event) => setForm({ ...form, account_id: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="">File it later</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                    {account.code ? ` (${account.code})` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-[#062B1A]/50">
                <ArrowLeftRight className="mt-0.5 size-3 shrink-0" />
                This payment appears in Transactions on its own — recording it here is the
                only entry needed. The account says where the money landed.
              </p>
            </div>}

            <div>
              <Label htmlFor="payment-note">Note</Label>
              <Input
                id="payment-note"
                value={form.note}
                onChange={(event) => setForm({ ...form, note: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>

            {/* What this payment does to the invoice, before it is taken. */}
            {!payment && <div className="rounded-lg border border-[rgba(6,43,26,0.1)] bg-[#F7F8FA] px-3 py-2 text-xs text-[#062B1A]/70">
              {amountCents <= 0 ? (
                'Enter an amount to see what this leaves outstanding.'
              ) : overpaying ? (
                <>
                  This is {formatCents(amountCents - dueCents)} more than this invoice owes. The
                  surplus stays as credit against the customer&apos;s other invoices rather than
                  being written off.
                </>
              ) : (
                <>
                  Leaves <strong>{formatCents(remaining)}</strong> outstanding —{' '}
                  {remaining === 0 ? 'the invoice will read Paid.' : 'the invoice will read Partial.'}
                </>
              )}
            </div>}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || amountCents <= 0 || !form?.payment_date}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {payment ? 'Save changes' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
